/**
 * Publisher-page warm pipeline:
 * - <link rel="preload"> for play shell (JS/CSS)
 * - Deduped image preload (poster, logo, play icons, articles)
 * - Hidden play-host iframe so video bytes are fetched from an allowed Referer
 */

import { preloadImages, warmDefaultPlayIcons } from "@/modules/assetImageCache";
import {
  normalizeMediaHostForPreconnect,
  resolveMediaServerHost,
} from "@/modules/experienceMediaUrls";
import { normalizeExperienceBody } from "@/modules/experiencePayloadUtils";
import { warmPlayShell } from "@/modules/playShellWarm";
import { collectExperienceImageUrls } from "@/modules/experienceImageUrls";
import { warmViaPlayIframe } from "@/modules/publisherWarmIframe";
import type { HostPixmoto, MediaWarmEntry } from "@/types/globals";

export { warmPlayShell } from "@/modules/playShellWarm";
export { collectExperienceImageUrls } from "@/modules/experienceImageUrls";
export { releasePublisherWarmVideo } from "@/modules/publisherWarmIframe";

/** Hosts and device the warm builds URLs for; callers may override any field. */
export interface WarmConfig {
  protocol: string;
  assetServer: string;
  sharedServer: string;
  mediaServer: string;
  playServer: string | null;
  device: string;
}

export interface WaitOptions {
  minStage?: string;
  timeout?: number;
}

const _preconnectHosts: Record<string, boolean> = {};
/** Stage the overlay needs before iframe src (video continues in background). */
export const OVERLAY_OPEN_MIN_STAGE = "images";

export const MEDIA_WARM_STAGE_ORDER = ["images", "video", "ready"];

function _detectDevice(): string {
  try {
    if (typeof navigator === "undefined" || !navigator.userAgent) {
      return "desktop";
    }
    const ua = navigator.userAgent;
    // Mirror config._isMobile so the warmed rendition matches what playback
    // requests — including touch laptops / newer iPads that report a desktop UA.
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        ua,
      ) ||
      (navigator.maxTouchPoints > 1 && /Mac|Windows/.test(ua) === false)
    ) {
      return "mobile";
    }
  } catch (_e) {
    /* ignore */
  }
  return "desktop";
}

function _warmConfig(): WarmConfig {
  const pm: HostPixmoto =
    typeof window !== "undefined" && window.__pixmoto ? window.__pixmoto : {};
  return {
    protocol: typeof location !== "undefined" ? location.protocol : "https:",
    assetServer: pm.assetServer || "assets.pixmoto.com",
    sharedServer: pm.sharedServer || "cdn.pixmoto.com",
    mediaServer: resolveMediaServerHost(pm),
    playServer: pm.playServer || null,
    device: pm.device || _detectDevice(),
  };
}

function _mediaWarmBucket(): Record<string, MediaWarmEntry | undefined> {
  const pm = window.__pixmoto || {};
  window.__pixmoto = pm;
  if (!pm.mediaWarm) {
    pm.mediaWarm = {};
  }
  return pm.mediaWarm;
}

function _preloadLinkId(url: string): string {
  return (
    "pix-preload-" +
    String(url)
      .replace(/[^a-z0-9]/gi, "-")
      .slice(0, 72)
  );
}

function _injectPreloadLink(url: string, as?: string) {
  if (!url || typeof document === "undefined") {
    return;
  }
  const id = _preloadLinkId(url);
  if (document.getElementById(id)) {
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "preload";
  link.as = as || "fetch";
  link.href = url;
  document.head.appendChild(link);
}

function _injectPreloadLinks(urls: string[], as?: string) {
  if (!urls || !urls.length) {
    return;
  }
  for (let i = 0; i < urls.length; i++) {
    _injectPreloadLink(urls[i], as);
  }
}

function _injectMediaPreconnect(mediaHost: string) {
  if (
    typeof document === "undefined" ||
    !mediaHost ||
    _preconnectHosts[mediaHost]
  ) {
    return;
  }
  _preconnectHosts[mediaHost] = true;
  const proto = typeof location !== "undefined" ? location.protocol : "https:";
  const id = `pix-prefetch-media-${mediaHost.replace(/[^a-z0-9]/gi, "-")}`;
  if (document.getElementById(id)) {
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "preconnect";
  link.href = `${proto}//${mediaHost}`;
  document.head.appendChild(link);
}

function _stageSatisfied(
  stages: MediaWarmEntry["stages"] | undefined,
  minStage: string,
): boolean {
  if (!stages) {
    return false;
  }
  if (minStage === "ready") {
    return stages.ready === "ok";
  }
  if (minStage === "video") {
    return stages.video === "ok" || stages.video === "error";
  }
  if (minStage === "images") {
    return stages.images === "ok" || stages.images === "error";
  }
  return false;
}

function _runMediaWarmPipeline(
  cacheKey: string,
  apiJson: unknown,
  entry: MediaWarmEntry,
  cfg: WarmConfig,
): Promise<void> {
  const body = normalizeExperienceBody(apiJson);
  if (!body || !body.video) {
    entry.stages = {
      api: "ok",
      images: "error",
      video: "error",
      ready: "error",
    };
    return Promise.resolve();
  }

  if (cfg.playServer) {
    _injectMediaPreconnect(cfg.playServer);
  }
  if (cfg.mediaServer) {
    // Click-time overlay iframe (top-level publisher origin) still fetches
    // from media.pixmoto.com directly; warm that connection too.
    _injectMediaPreconnect(normalizeMediaHostForPreconnect(cfg.mediaServer));
  }

  let imagesPromise: Promise<void>;
  if (entry.stages.images === "ok") {
    imagesPromise = Promise.resolve();
  } else {
    imagesPromise = warmDefaultPlayIcons(cfg.sharedServer)
      .then(function () {
        const urls = collectExperienceImageUrls(body, cfg);
        _injectPreloadLinks(urls, "image");
        return preloadImages(urls);
      })
      .then(
        function () {
          entry.stages.images = "ok";
        },
        function () {
          entry.stages.images = "error";
        },
      );
  }

  let videoPromise: Promise<boolean>;
  if (entry.stages.video === "ok") {
    videoPromise = Promise.resolve(true);
  } else if (!cfg.playServer || (!body.idexperience && !body.url)) {
    entry.stages.video = "error";
    videoPromise = Promise.resolve(false);
  } else {
    videoPromise = warmViaPlayIframe(
      cfg.playServer,
      body.idexperience,
      body.url,
      cfg,
    ).then(function (ok) {
      entry.stages.video = ok ? "ok" : "error";
      return ok;
    });
  }

  return Promise.all([imagesPromise, videoPromise]).then(function () {
    entry.stages.ready = entry.stages.images === "ok" ? "ok" : "error";
  });
}

export function startMediaWarm(
  cacheKey: string,
  apiJson: unknown,
  opts?: Partial<WarmConfig> & WaitOptions,
): Promise<void> {
  if (typeof window === "undefined" || !cacheKey) {
    return Promise.resolve();
  }

  const body = apiJson ? normalizeExperienceBody(apiJson) : null;
  const idKey =
    body && body.idexperience != null ? String(body.idexperience) : null;
  const bucket = _mediaWarmBucket();
  let entry: MediaWarmEntry | null | undefined = null;

  if (idKey && bucket[idKey]) {
    entry = bucket[idKey];
    cacheKey = idKey;
  } else if (bucket[cacheKey]) {
    entry = bucket[cacheKey];
  }

  if (entry && entry.stages && entry.stages.ready === "ok") {
    return entry.promise || Promise.resolve();
  }
  if (entry && entry._inflightPromise) {
    return entry._inflightPromise;
  }

  if (!entry) {
    entry = {
      stages: {
        api: "ok",
        images: "pending",
        video: "pending",
        ready: "pending",
      },
      videoUrl: null,
    };
  }

  bucket[cacheKey] = entry;
  if (idKey) {
    bucket[idKey] = entry;
  }

  const cfg = Object.assign({}, _warmConfig(), opts || {});

  entry._inflightPromise = _runMediaWarmPipeline(
    cacheKey,
    apiJson,
    entry,
    cfg,
  ).finally(function () {
    delete entry._inflightPromise;
  });

  entry.promise = entry._inflightPromise;

  return entry._inflightPromise;
}

export function getMediaWarm(cacheKey: string): MediaWarmEntry | undefined {
  return _mediaWarmBucket()[cacheKey];
}

export function isMediaWarmStageReady(
  cacheKey: string,
  minStage: string,
): boolean {
  const entry = getMediaWarm(cacheKey);
  return !!(entry && entry.stages && _stageSatisfied(entry.stages, minStage));
}

export function waitForMediaWarm(
  cacheKey: string,
  opts?: WaitOptions,
): Promise<void> {
  opts = opts || {};
  const minStage = opts.minStage || "ready";
  const timeout = opts.timeout != null ? opts.timeout : 10000;

  const bucket = _mediaWarmBucket();
  const entry = bucket[cacheKey];

  if (!entry) {
    const pref =
      typeof window !== "undefined" &&
      window.__pixmoto &&
      window.__pixmoto.prefetchedExperiences
        ? window.__pixmoto.prefetchedExperiences[cacheKey]
        : null;
    if (pref && pref.status === "ok" && pref.data) {
      return startMediaWarm(cacheKey, pref.data, opts).then(function () {
        return waitForMediaWarm(cacheKey, opts);
      });
    }
    return Promise.resolve();
  }

  if (_stageSatisfied(entry.stages, minStage)) {
    return Promise.resolve();
  }

  const waitTarget =
    entry.promise || entry._inflightPromise || Promise.resolve();

  return Promise.race([
    waitTarget.then(function () {
      if (!_stageSatisfied(entry.stages, minStage)) {
        return waitForMediaWarm(cacheKey, opts);
      }
    }),
    new Promise(function (resolve) {
      setTimeout(resolve, timeout);
    }),
  ]) as Promise<void>;
}

/**
 * Insert preload hints + fetch play bundle (once per host).
 */
export function injectPlayShellPreloadHints(
  playHost: string | null | undefined,
): Promise<void> {
  return warmPlayShell(playHost);
}

/**
 * Warm HTTP cache for imagery + staged video partial fetch.
 */
export function warmExperiencePayloadCaches(apiJson: unknown): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  const body = normalizeExperienceBody(apiJson);
  if (!body || !body.video) {
    return Promise.resolve();
  }
  const cacheKey = body.idexperience != null ? String(body.idexperience) : null;
  if (!cacheKey) {
    const cfg = _warmConfig();
    return warmDefaultPlayIcons(cfg.sharedServer).then(function () {
      const urls = collectExperienceImageUrls(body, cfg);
      return preloadImages(urls);
    });
  }
  return startMediaWarm(cacheKey, apiJson);
}
